import { USER_CREDIT_QUERY_KEY } from "@/components/Referral/hooks/useUserCreditQuery";
import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";
import { getCurrentUserToken } from "@/lib/auth";
import { changePlanInterval } from "@/lib/pricing/changePlanInterval";
import { TBillingInterval } from "@/types/app";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleIntervalChange } from "./handle-interval-change";

interface IntervalChangeMutationOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
  isV1?: boolean;
}

export function useIntervalChangeMutation(
  options?: IntervalChangeMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      targetInterval,
      promoCode,
      isReferralCreditUsed,
    }: {
      targetInterval: TBillingInterval;
      promoCode?: string;
      isReferralCreditUsed?: boolean;
    }) => {
      const token = await getCurrentUserToken();
      if (!token) throw new Error("No auth token");

      if (options?.isV1) {
        const v1Interval = targetInterval === "yearly" ? "year" : "month";
        try {
          await changePlanInterval({
            token,
            interval: v1Interval,
            ...(promoCode?.trim() ? { couponValue: promoCode.trim() } : {}),
          });
        } catch (error: any) {
          const errData = error.response?.data;
          if (errData?.redirect_url) {
            return {
              status: false,
              data: { message: errData.error ?? "" },
              error: {
                code: 302,
                message: errData.error ?? "",
                data: { redirect_url: errData.redirect_url },
              },
            } as any;
          }
          throw error;
        }
        return { status: true, data: { message: "" } };
      }

      const response = await handleIntervalChange({
        token,
        payload: {
          change_to: targetInterval,
          ...(promoCode?.trim() ? { discounts: [promoCode.trim()] } : {}),
          ...(isReferralCreditUsed ? { use_referral_credits: true } : {}),
        },
      });

      return response;
    },
    onSuccess: (response) => {
      if (response.status) {
        // Success - refetch user plan data and referral credits
        queryClient.refetchQueries({ queryKey: [USER_PLAN_QUERY_KEY] });
        queryClient.refetchQueries({ queryKey: [USER_CREDIT_QUERY_KEY] });
        options?.onSuccess?.();
      } else if (response.error?.code === 302) {
        // Redirect to payment page — onSuccess intentionally not called here
        const redirectUrl = response.error.data.redirect_url;
        window.location.href = redirectUrl;
      } else {
        options?.onError?.(
          new Error(response.error?.message ?? "Interval change failed"),
        );
      }
    },
    onError: (error) => {
      console.error("Error changing interval:", error);
      options?.onError?.(error);
    },
  });
}
